/**
 * ⚙ Advanced settings (§14.4): a cogwheel in the lobby opens the panel (closed by default).
 * The host edits; everyone else can open it read-only. The panel is generated from the config
 * package's settings metadata (`SETTINGS`), grouped as in §14.4.
 *
 * Values shown are the server's resolved config (the host's own pending edits on top). Every
 * edit replaces the host's overrides with `configure`; the server validates them, and its
 * errors and warnings (from `validateConfig`) appear next to the setting they concern. Start
 * stays disabled while there are errors (the lobby view's `canStart`). Settings of features not
 * in this version are shown disabled. Once the game starts, the settings are locked.
 */
import type { RoleId, TimerConfig } from "@handshake/config";
import {
  getPath,
  ROLE_IDS,
  ROLES,
  setPath,
  SETTING_GROUP_LABELS,
  SETTING_GROUPS,
  SETTINGS,
  settingsIn,
  SYNDICATE_ROLE_IDS,
  type SettingDef,
  type SettingGroup,
} from "@handshake/config/catalog";
import { useState, type ReactNode } from "react";
import { RuleChanges } from "../../components/RulesBadge.tsx";
import { Sheet } from "../../components/Sheet.tsx";
import { Stepper } from "../../components/Stepper.tsx";
import { useDraft } from "../../lib/draft.ts";
import {
  deleteSavedConfig,
  listSavedConfigs,
  saveConfig,
  type SavedConfig,
} from "../../lib/savedConfigs.ts";
import { send, toast } from "../../lib/store.ts";
import { useView } from "../../lib/view.ts";
import type { PublicView, RosterEntry } from "../../lib/types.ts";

type Overrides = Record<string, unknown>;
type Issue = { readonly path: string; readonly message: string };

/** The ⚙ button in the lobby. The panel opens in a sheet and is closed by default. */
export function AdvancedSettingsEntry() {
  const view = useView();
  const [open, setOpen] = useState(false);
  const rules = view.public.rules;
  const n = rules?.changes.length ?? 0;
  const canEdit = view.me.isHost && view.public.phase.kind === "lobby";
  return (
    <>
      <button
        type="button"
        className="btn block"
        data-testid="advanced-settings"
        onClick={() => setOpen(true)}
      >
        <span aria-hidden="true">⚙︎</span> Advanced settings
        {canEdit ? null : <span className="faint">(view only)</span>}
        {n > 0 ? <span className="tag">{n}</span> : null}
      </button>
      <Sheet
        open={open}
        title="⚙︎ Advanced settings"
        onClose={() => setOpen(false)}
        testId="advanced-panel"
      >
        <AdvancedSettings />
      </Sheet>
    </>
  );
}

/** A setting's issues: those whose path is the setting's path or inside it. */
function issuesFor(issues: readonly Issue[], path: string): Issue[] {
  return issues.filter((i) => i.path === path || i.path.startsWith(`${path}.`));
}

/** True when an issue belongs to one of the settings (else it is shown at the top). */
function isKnownPath(path: string): boolean {
  return SETTINGS.some((s) => path === s.path || path.startsWith(`${s.path}.`));
}

export function AdvancedSettings() {
  const view = useView();
  const pub = view.public;
  const config = pub.config;
  const lobby = pub.lobby;
  const inLobby = pub.phase.kind === "lobby";
  const canEdit = view.me.isHost && inLobby;
  const remote = (view.lobbyOverrides ?? {}) as Overrides;
  const [draft, setDraft] = useDraft<Overrides>(remote, async (overrides) => {
    const ack = await send({
      type: "configure",
      overrides,
      playerCount: lobby?.playerCount ?? null,
    });
    return ack.ok;
  });

  if (!inLobby) {
    return (
      <div className="stack">
        <p className="muted" data-testid="settings-locked">
          The settings are locked once the game starts. Only the timers can change during play.
        </p>
        {pub.rules ? <RuleChanges rules={pub.rules} /> : null}
      </div>
    );
  }
  if (!config) {
    return (
      <p className="muted">
        The settings appear once enough guests have joined for a setup (at least 6).
      </p>
    );
  }

  const value = (path: string): unknown => {
    if (canEdit) {
      const v = getPath(draft, path);
      if (v !== undefined) return v;
    }
    return getPath(config, path);
  };
  const set = (path: string, v: unknown) => setDraft(setPath(draft, path, v));
  const errors = lobby?.errors ?? [];
  const warnings = lobby?.warnings ?? [];
  const changes = pub.rules?.changes ?? [];
  const general = [...errors, ...warnings].filter((i) => !isKnownPath(i.path));

  return (
    <div className="stack" data-testid="advanced-settings-form">
      <p className="faint" style={{ margin: 0 }}>
        {canEdit
          ? "Every change is visible to everyone: the rules badge counts the changes from the defaults."
          : "Only the host can change these settings. Every change shows on the rules badge."}
      </p>
      <div className="row wrap">
        <strong data-testid="settings-badge">{pub.rules?.badge ?? "Standard rules"}</strong>
        {pub.rules?.experimental ? <span className="tag">Experimental</span> : null}
        {pub.rules?.rigged ? <span className="tag">Rigged game</span> : null}
      </div>
      {errors.length > 0 ? (
        <p className="error-text" data-testid="settings-errors" style={{ margin: 0 }}>
          {errors.length === 1 ? "1 problem blocks" : `${errors.length} problems block`} the start.
        </p>
      ) : null}
      {general.length > 0 ? <IssueList issues={general} errors={errors} /> : null}

      {SETTING_GROUPS.map((group) => (
        <SettingGroupPanel
          key={group}
          group={group}
          changed={changes.filter((c) => c.path.split(".")[0] === group).length}
          issues={[...errors, ...warnings].filter((i) => i.path.split(".")[0] === group).length}
        >
          {settingsIn(group).map((def) => (
            <SettingRow
              key={def.path}
              def={def}
              value={value}
              set={set}
              canEdit={canEdit && def.available}
              errors={issuesFor(errors, def.path)}
              warnings={issuesFor(warnings, def.path)}
              change={changes.find((c) => c.path === def.path || c.path.startsWith(`${def.path}.`))}
              onReset={
                canEdit && getPath(draft, def.path) !== undefined
                  ? () => set(def.path, undefined)
                  : undefined
              }
              pub={pub}
            />
          ))}
        </SettingGroupPanel>
      ))}

      <section className="panel" data-testid="settings-changes">
        <h3>Changes from the defaults</h3>
        {pub.rules ? <RuleChanges rules={pub.rules} /> : null}
      </section>

      {canEdit ? (
        <>
          <button
            type="button"
            className="btn block"
            data-testid="settings-reset"
            disabled={Object.keys(draft).filter((k) => k !== "presetId").length === 0}
            onClick={() => setDraft(draft.presetId != null ? { presetId: draft.presetId } : {})}
          >
            Reset to defaults
          </button>
          <SavedConfigs
            overrides={draft}
            playerCount={lobby?.playerCount ?? null}
            timers={pub.timers}
          />
        </>
      ) : null}
    </div>
  );
}

function SettingGroupPanel({
  group,
  changed,
  issues,
  children,
}: {
  group: SettingGroup;
  changed: number;
  issues: number;
  children: ReactNode;
}) {
  return (
    <details className="panel settings-group" data-testid={`settings-group-${group}`}>
      <summary>
        <strong>{SETTING_GROUP_LABELS[group]}</strong>
        {changed > 0 ? <span className="tag">{changed} changed</span> : null}
        {issues > 0 ? <span className="tag danger">{issues} to check</span> : null}
      </summary>
      <ul className="list">{children}</ul>
    </details>
  );
}

function IssueList({ issues, errors }: { issues: readonly Issue[]; errors: readonly Issue[] }) {
  return (
    <ul className="issues">
      {issues.map((i, k) => (
        <li key={k} className={errors.includes(i) ? "error-text" : "warning-text"}>
          {i.message}
        </li>
      ))}
    </ul>
  );
}

const testIdOf = (path: string) => `setting-${path.replace(/\./g, "-")}`;

function SettingRow({
  def,
  value,
  set,
  canEdit,
  errors,
  warnings,
  change,
  onReset,
  pub,
}: {
  def: SettingDef;
  value: (path: string) => unknown;
  set: (path: string, v: unknown) => void;
  canEdit: boolean;
  errors: readonly Issue[];
  warnings: readonly Issue[];
  change: { readonly from: string; readonly to: string } | undefined;
  onReset: (() => void) | undefined;
  pub: PublicView;
}) {
  return (
    <li
      className="setting"
      data-testid={testIdOf(def.path)}
      data-changed={change ? "true" : "false"}
    >
      <div className="row">
        <span className="grow">
          <span className="setting-label">{def.label}</span>
          {change ? <span className="tag">changed</span> : null}
        </span>
        {onReset ? (
          <button
            type="button"
            className="btn small ghost"
            aria-label={`Reset ${def.label}`}
            onClick={onReset}
          >
            ↺
          </button>
        ) : null}
      </div>
      {def.help ? <div className="faint">{def.help}</div> : null}
      {!def.available ? <div className="faint">Not available in this version.</div> : null}
      {change ? <div className="faint">Default: {change.from}</div> : null}
      <SettingControl def={def} value={value} set={set} disabled={!canEdit} pub={pub} />
      {errors.map((e, i) => (
        <div key={`e${i}`} className="error-text" role="alert">
          {e.message}
        </div>
      ))}
      {warnings.map((w, i) => (
        <div key={`w${i}`} className="warning-text">
          {w.message}
        </div>
      ))}
    </li>
  );
}

function Toggle({
  checked,
  onChange,
  disabled,
  label,
  testId,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled: boolean;
  label: string;
  testId?: string;
}) {
  return (
    <label className="switch">
      <input
        type="checkbox"
        role="switch"
        checked={checked}
        disabled={disabled}
        aria-label={label}
        data-testid={testId}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>{checked ? "On" : "Off"}</span>
    </label>
  );
}

function SettingControl({
  def,
  value,
  set,
  disabled,
  pub,
}: {
  def: SettingDef;
  value: (path: string) => unknown;
  set: (path: string, v: unknown) => void;
  disabled: boolean;
  pub: PublicView;
}) {
  const c = def.control;
  const v = value(def.path);
  const id = testIdOf(def.path);
  switch (c.type) {
    case "int":
      return (
        <Stepper
          value={Number(v)}
          min={c.min}
          max={c.max}
          step={c.unit === "%" ? 5 : 1}
          label={def.label}
          format={(n) => (c.unit === "%" ? `${n} %` : String(n))}
          onChange={(n) => set(def.path, n)}
          disabled={disabled}
          testId={`${id}-stepper`}
        />
      );
    case "bool":
      return (
        <Toggle
          checked={v === true}
          disabled={disabled}
          label={def.label}
          onChange={(b) => set(def.path, b)}
          testId={`${id}-toggle`}
        />
      );
    case "select":
      return (
        <select
          className="text"
          value={String(v)}
          disabled={disabled}
          aria-label={def.label}
          data-testid={`${id}-select`}
          onChange={(e) => {
            const opt = c.options.find((o) => String(o.value) === e.target.value);
            if (opt) set(def.path, opt.value);
          }}
        >
          {c.options.map((o) => (
            <option key={String(o.value)} value={String(o.value)}>
              {o.label}
            </option>
          ))}
        </select>
      );
    case "range": {
      const r = (v ?? { min: 0, max: 0 }) as { min: number; max: number };
      return (
        <div className="row wrap">
          <span className="faint">from</span>
          <Stepper
            value={r.min}
            min={c.min}
            max={c.max}
            label={`${def.label} minimum`}
            disabled={disabled}
            onChange={(n) => set(def.path, { min: n, max: Math.max(n, r.max) })}
          />
          <span className="faint">to</span>
          <Stepper
            value={r.max}
            min={c.min}
            max={c.max}
            label={`${def.label} maximum`}
            disabled={disabled}
            onChange={(n) => set(def.path, { min: Math.min(n, r.min), max: n })}
          />
        </div>
      );
    }
    case "perRole":
      return <PerRoleEditor def={def} value={value} set={set} disabled={disabled} />;
    case "bugTruth": {
      const scramble = v === "scramble";
      return (
        <div className="row wrap">
          <select
            className="text"
            style={{ width: "auto" }}
            value={scramble ? "scramble" : "chance"}
            disabled={disabled}
            aria-label={def.label}
            onChange={(e) => set(def.path, e.target.value === "scramble" ? "scramble" : 50)}
          >
            <option value="scramble">Scramble</option>
            <option value="chance">Fixed chance</option>
          </select>
          {!scramble ? (
            <Stepper
              value={Number(v)}
              min={0}
              max={100}
              step={5}
              label={`${def.label} (percent)`}
              format={(n) => `${n} %`}
              disabled={disabled}
              onChange={(n) => set(def.path, n)}
            />
          ) : null}
        </div>
      );
    }
    case "optionalSeconds": {
      const on = typeof v === "number";
      return (
        <div className="row wrap">
          <Toggle
            checked={on}
            disabled={disabled}
            label={def.label}
            onChange={(b) => set(def.path, b ? c.min : null)}
          />
          {on ? (
            <Stepper
              value={v}
              min={c.min}
              max={c.max}
              step={30}
              label={`${def.label} delay`}
              format={(n) => `${n} s`}
              disabled={disabled}
              onChange={(n) => set(def.path, n)}
            />
          ) : null}
        </div>
      );
    }
    case "text":
      return (
        <input
          className="text"
          type="text"
          maxLength={c.maxLength}
          value={typeof v === "string" ? v : ""}
          placeholder={disabled && def.path === "testing.seed" ? "hidden or none" : "none"}
          disabled={disabled}
          aria-label={def.label}
          data-testid={`${id}-input`}
          onChange={(e) => set(def.path, e.target.value.trim() === "" ? null : e.target.value)}
        />
      );
    case "roleSet":
      return <RoleSetEditor def={def} value={v as RoleId[]} set={set} disabled={disabled} />;
    case "roleList":
      return (
        <RoleListEditor
          value={v as RoleId[] | null}
          set={(roles) => set(def.path, roles)}
          disabled={disabled}
          playerCount={Number(value("playerCount") ?? pub.config?.playerCount ?? 6)}
          agents={Number(value("teams.agents"))}
        />
      );
    case "forcedRoles":
      return (
        <ForcedRolesEditor
          value={(v ?? []) as { player: string; role: RoleId }[]}
          set={(list) => set(def.path, list)}
          disabled={disabled}
          roster={pub.roster}
        />
      );
  }
}

function PerRoleEditor({
  def,
  value,
  set,
  disabled,
}: {
  def: SettingDef;
  value: (path: string) => unknown;
  set: (path: string, v: unknown) => void;
  disabled: boolean;
}) {
  if (def.control.type !== "perRole") return null;
  const { min, max } = def.control;
  const base = Number(value(`${def.path}.base`));
  const roles = def.path.startsWith("cards.siphons") ? SYNDICATE_ROLE_IDS : ROLE_IDS;
  return (
    <div className="stack" style={{ marginTop: "0.4rem" }}>
      <div className="row">
        <span className="grow faint">Every role</span>
        <Stepper
          value={base}
          min={min}
          max={max}
          label={def.label}
          disabled={disabled}
          onChange={(n) => set(`${def.path}.base`, n)}
          testId={`${testIdOf(def.path)}-base`}
        />
      </div>
      <details>
        <summary className="faint" style={{ cursor: "pointer", minHeight: "2.2rem" }}>
          By role
        </summary>
        <ul className="list">
          {roles.map((role) => {
            const own = value(`${def.path}.byRole.${role}`);
            return (
              <li key={role}>
                <span className="grow">
                  {ROLES[role].name}
                  {typeof own !== "number" ? <span className="faint"> (every role)</span> : null}
                </span>
                <Stepper
                  value={typeof own === "number" ? own : base}
                  min={min}
                  max={max}
                  label={`${def.label}: ${ROLES[role].name}`}
                  disabled={disabled}
                  onChange={(n) => set(`${def.path}.byRole.${role}`, n)}
                />
              </li>
            );
          })}
        </ul>
      </details>
    </div>
  );
}

function RoleSetEditor({
  def,
  value,
  set,
  disabled,
}: {
  def: SettingDef;
  value: readonly RoleId[];
  set: (path: string, v: unknown) => void;
  disabled: boolean;
}) {
  const inPool = new Set(value);
  return (
    <div className="role-grid">
      {ROLE_IDS.map((role) => (
        <label key={role} className={`role-check ${ROLES[role].team}`}>
          <input
            type="checkbox"
            checked={inPool.has(role)}
            disabled={disabled}
            onChange={(e) =>
              set(
                def.path,
                ROLE_IDS.filter((r) => (r === role ? e.target.checked : inPool.has(r))),
              )
            }
          />
          <span>{ROLES[role].name}</span>
        </label>
      ))}
    </div>
  );
}

/** A starting role list for "Choose roles": Attachés and the Syndicate roles in order. */
function starterRoles(playerCount: number, agents: number): RoleId[] {
  const syndicate = Array.from(
    { length: agents },
    (_, i) => SYNDICATE_ROLE_IDS[i % SYNDICATE_ROLE_IDS.length] as RoleId,
  );
  const delegates = Array.from(
    { length: Math.max(0, playerCount - agents) },
    (): RoleId => "attache",
  );
  return [...delegates, ...syndicate];
}

function RoleListEditor({
  value,
  set,
  disabled,
  playerCount,
  agents,
}: {
  value: RoleId[] | null;
  set: (roles: RoleId[] | null) => void;
  disabled: boolean;
  playerCount: number;
  agents: number;
}) {
  const explicit = value !== null;
  const counts = new Map<RoleId, number>();
  for (const r of value ?? []) counts.set(r, (counts.get(r) ?? 0) + 1);
  const total = value?.length ?? 0;
  const syndicate = (value ?? []).filter((r) => ROLES[r].team === "syndicate").length;
  const setCount = (role: RoleId, n: number) => {
    const list = ROLE_IDS.flatMap((r) =>
      Array.from({ length: r === role ? n : (counts.get(r) ?? 0) }, () => r),
    );
    set(list);
  };
  return (
    <div className="stack" style={{ marginTop: "0.4rem" }}>
      <div className="toggle">
        <button
          type="button"
          className="btn small"
          aria-pressed={!explicit}
          disabled={disabled}
          onClick={() => set(null)}
        >
          Random within budget
        </button>
        <button
          type="button"
          className="btn small"
          aria-pressed={explicit}
          disabled={disabled}
          data-testid="roles-explicit"
          onClick={() => {
            if (!explicit) set(starterRoles(playerCount, agents));
          }}
        >
          Choose roles
        </button>
      </div>
      {explicit ? (
        <>
          <div className="faint" data-testid="roles-count">
            {total} of {playerCount} roles · {syndicate} Syndicate for {agents} Agents
          </div>
          <ul className="list">
            {ROLE_IDS.map((role) => (
              <li key={role}>
                <span className="grow">
                  {ROLES[role].name}
                  <span className="faint">
                    {" "}
                    · {ROLES[role].team === "syndicate" ? "Syndicate" : "Delegate"}
                  </span>
                </span>
                <Stepper
                  value={counts.get(role) ?? 0}
                  min={0}
                  max={playerCount}
                  label={`Number of ${ROLES[role].name}`}
                  disabled={disabled}
                  onChange={(n) => setCount(role, n)}
                />
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </div>
  );
}

function ForcedRolesEditor({
  value,
  set,
  disabled,
  roster,
}: {
  value: readonly { player: string; role: RoleId }[];
  set: (list: { player: string; role: RoleId }[]) => void;
  disabled: boolean;
  roster: readonly RosterEntry[];
}) {
  const nameOf = (id: string) => roster.find((p) => p.id === id)?.name ?? id;
  const free = roster.filter((p) => !value.some((f) => f.player === p.id));
  return (
    <div className="stack" style={{ marginTop: "0.4rem" }}>
      {value.length === 0 ? <div className="faint">None.</div> : null}
      {value.map((f, i) => (
        <div key={f.player} className="row wrap">
          <span className="grow">{nameOf(f.player)}</span>
          <select
            className="text"
            style={{ width: "auto" }}
            value={f.role}
            disabled={disabled}
            aria-label={`Role forced on ${nameOf(f.player)}`}
            onChange={(e) =>
              set(value.map((x, k) => (k === i ? { ...x, role: e.target.value as RoleId } : x)))
            }
          >
            {ROLE_IDS.map((r) => (
              <option key={r} value={r}>
                {ROLES[r].name}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn small ghost"
            disabled={disabled}
            aria-label={`Remove the forced role of ${nameOf(f.player)}`}
            onClick={() => set(value.filter((_, k) => k !== i))}
          >
            ✕
          </button>
        </div>
      ))}
      {!disabled && free.length > 0 ? (
        <select
          className="text"
          value=""
          aria-label="Force a role on a player"
          onChange={(e) => {
            if (e.target.value) set([...value, { player: e.target.value, role: "attache" }]);
          }}
        >
          <option value="">Force a role on…</option>
          {free.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      ) : null}
    </div>
  );
}

function SavedConfigs({
  overrides,
  playerCount,
  timers,
}: {
  overrides: Overrides;
  playerCount: number | null;
  timers: TimerConfig;
}) {
  const [list, setList] = useState<SavedConfig[]>(listSavedConfigs);
  const [name, setName] = useState("");
  const load = async (s: SavedConfig) => {
    const a = await send({
      type: "configure",
      overrides: { ...s.overrides },
      playerCount: s.playerCount,
    });
    const b = await send({ type: "setTimers", timers: s.timers });
    if (a.ok && b.ok) toast(`Loaded "${s.name}".`);
  };
  return (
    <section className="panel stack" data-testid="saved-configs">
      <h3 style={{ margin: 0 }}>Saved on this phone</h3>
      <form
        className="row"
        onSubmit={(e) => {
          e.preventDefault();
          if (!name.trim()) return;
          setList(saveConfig({ name, savedAt: Date.now(), overrides, playerCount, timers }));
          toast(`Saved "${name.trim()}" on this phone.`);
          setName("");
        }}
      >
        <input
          className="text grow"
          placeholder="Name this configuration"
          maxLength={40}
          value={name}
          data-testid="save-config-name"
          onChange={(e) => setName(e.target.value)}
        />
        <button type="submit" className="btn" disabled={!name.trim()} data-testid="save-config">
          Save
        </button>
      </form>
      {list.length === 0 ? (
        <p className="faint" style={{ margin: 0 }}>
          Nothing saved yet. Saved configurations include the timers.
        </p>
      ) : (
        <ul className="list">
          {list.map((s) => (
            <li key={s.name}>
              <span className="grow">{s.name}</span>
              <button
                type="button"
                className="btn small"
                data-testid={`load-config-${s.name}`}
                onClick={() => void load(s)}
              >
                Load
              </button>
              <button
                type="button"
                className="btn small ghost"
                aria-label={`Delete ${s.name}`}
                onClick={() => setList(deleteSavedConfig(s.name))}
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
